// THE TUTORIALS. One per role, in the same shape every time (Matt, after
// playtest 9): "this is what your character does, this is why. Here are the
// choices you have, here's what those choices mean, and how to act on each
// of them. And it needs to be well-written and straightforward and clean and
// not too much text." Brian's idea for the how: play it, don't read it -
// "a little blinking button that's like, do this."
//
// A step is one of:
//   card  - a plain card in the middle of the screen (the opening, the
//           role, the choices, the end). `list` is [term, line] pairs.
//   coach - a spotlight on part of the real board (`target`, a CSS
//           selector) and a short line under it.
// A step moves on by a button (`next`), or by the thing it asked for:
// `waitGame(game, role)` (the practice game shows it), `waitAct` (that
// action of the role lands in the practice log) or
// `waitDom` (an element appears, e.g. a picked-up card). `skip` offers a
// way past a wait the board might not allow. `needs` ({ dom, target, text })
// is what the step relies on staying on screen; while it is gone the coach
// points at `target` and says `text` instead. `then` ({ dom, target, text })
// is the second half of a two-part step: once `dom` shows, the coach points
// there and says that instead.
//
// Rules for the words: second person, present tense, one idea a line, no
// line over about twenty words. Name the button exactly as the screen does.

import { ROLE_INFO } from '../engine/constants.js';
import { makeTutorialGame } from './sandbox.js';

// How many workers the practice dawn has to share (the same seed every time).
let practiceWorkers;
const workersToday = () => {
  if (practiceWorkers === undefined) practiceWorkers = makeTutorialGame('culture', 'count').civ.workers;
  return practiceWorkers;
};

const OPEN = (label) => ({
  kind: 'card',
  title: `Learn the ${label}`,
  // Playtest 11, Nick, on "a practice copy, on your phone only": "I don't
  // know what that means." Say what it is and what to do with it.
  text: 'A practice day on a copy of the game, on this phone only. Nothing here counts. The real game waits until you finish.',
  next: 'Start',
});

// THE BIG PICTURE, before your own board (the learner, 2026-10-12: every
// run forgot what its role was for and had no idea what the others did;
// Matt: "make it clear what the roles are each doing... clearly explain how
// the dawn works and how workers are distributed and what a single worker
// means to any role").
const ORDER = ['culture', 'trade', 'science', 'military'];
const fourList = (held) => ORDER.map((r) => [held.includes(r) ? `${ROLE_INFO[r].label} (you)` : ROLE_INFO[r].label, ROLE_INFO[r].job]);
const FOUR = (role) => ({
  kind: 'card',
  title: 'Four roles, one town',
  text: 'Each player holds a role. You all play at the same time, and every role needs the others.',
  list: fourList([role]),
  // The coach passes every role this phone holds in the real game, so a
  // player with two is told both (playtest kimZa: "it says that I'm the Hall
  // although I'm also the Stewards").
  listFor: (held) => fourList(held.includes(role) ? held : [role, ...held]),
  next: 'Next',
});
const DAWN = (role) => ({
  kind: 'card',
  title: 'Every morning: dawn',
  text: 'Each day starts at dawn, before anyone works. The table shares the town workers out between the four roles; anyone can move the split. Then everyone plays at once.',
  // (The "Ask for…" button this once named is gone; people just talk - playtest 11.)
  why: `${ROLE_INFO[role].worker} Short of workers? Say so; the table decides the split together.`,
  next: 'Try it',
});
// ...and then DO it: the practice game opens at dawn with one worker still
// to give out (sandbox.js). The board under the split is yours, priced with
// the split as it stands, so giving the worker shows what it buys.
// Playtest 14: the practice dawn "feels a very false example". So it says
// what is true - day one's split is dealt by the game, and for practice
// every worker is yours - and has you give the last one so the + is learned.
const GIVE = (role) => {
  const n = workersToday();
  const label = ROLE_INFO[role].label;
  return {
    kind: 'coach',
    target: '[data-tut="alloc-mine"]',
    text: `Day one's split is dealt by the game; from day two the table moves it with + and −. For practice all ${n} are yours: tap + by the ${label}.`,
    waitGame: (g, r) => g.meta.phase !== 'dawn' || (g.allocation?.[r] || 0) >= g.civ.workers,
    skip: 'Skip',
  };
};
// What ONE WORKER is on each board (playtest 14: "workers mean different
// things per board" was the thing the tutorial never said).
const WORKER_CARD = {
  kind: 'card',
  title: 'What a worker is',
  text: 'The same person means something different on each board.',
  list: ORDER.map((r) => [ROLE_INFO[r].label, ROLE_INFO[r].worker.replace(/^For the \w+, /, '').replace(/^\w/, (c) => c.toUpperCase())]),
  next: 'Next',
};
const START = {
  kind: 'coach',
  target: '[data-tut="alloc-start"]',
  text: 'Tap Start the day.',
  waitGame: (g) => g.meta.phase !== 'dawn',
};

const DONE_STEP = {
  kind: 'coach',
  target: '[data-tut="done"]',
  // Not "tap Done" with workers in hand (Matt, playtest 10: "why would I?").
  text: 'Workers left? Use them first. Then tap *Done* for the day. All four roles play at once; the day ends when everyone is done.',
  next: 'Next',
  // Tapping Done moves the tutorial on: the practice game has no others to
  // wait for, and 'Done, waiting on others' sat there forever (playtest WG4y0).
  waitGame: (g, role) => Boolean(g.done?.[role]),
};

const END = (label, job, recap) => ({
  kind: 'card',
  title: `The ${label}`,
  text: `Your job: ${job}`,
  why: recap,
  foot: 'Run it again any time: the menu button in the top-right corner, then Help, then Tutorial.',
  next: 'Back to the game',
});

export const TUTORIALS = {
  culture: {
    label: 'Hall',
    steps: [
      OPEN('Hall'),
      FOUR('culture'),
      DAWN('culture'),
      WORKER_CARD,
      GIVE('culture'),
      START,
      {
        kind: 'card',
        title: 'What the Hall does',
        text: 'You lead the town. You keep people happy, grow the town, and hand out extra workers.',
        why: 'Why it matters: an unhappy town works at half strength, and at zero happiness the game is lost.',
        next: 'Next',
      },
      {
        // Short on purpose (playtest 14: the Hall tutorial "didn't work at
        // all" for a newcomer; "there's so much stuff"). Four lines, one
        // idea each; the rest is shown on the board, not read here.
        kind: 'card',
        title: 'Your choices',
        list: [
          ['Your hand', 'Dealt when the day starts: 2 cards for each worker the Hall was given.'],
          ['Play a card', 'Tap a card, then a counselor. It is free, and does what it says.'],
          ['Counselors', 'Each likes one kind of card. Play them one they like and they add a bonus.'],
          ['Buy a card', 'One a day, from the shelf. That is the only time a card costs anything.'],
        ],
        next: 'Show me',
      },
      {
        kind: 'coach',
        target: '[data-tut="hall-hand"]',
        text: 'This is your hand. A small gold heart means a counselor likes that card. Tap a card to pick it up.',
        waitDom: '.fan__card--up',
      },
      {
        kind: 'coach',
        target: '[data-tut="hall-council"]',
        // Playtest 11, Nick with a card up: "should I just give somebody
        // something? I don't know what any of these mean."
        text: 'Your council. Each chair says what it likes. A lit chair likes the card in your hand. Tap any chair to play the card.',
        needs: { dom: '.fan__card--up', target: '[data-tut="hall-hand"]', text: 'Pick a card up first: tap one in your hand. Tapping it again puts it back.' },
        waitAct: 'play',
        skip: 'Skip',
      },
      {
        kind: 'coach',
        target: '[data-tut="hall-council"]',
        text: 'Each chair takes one card a day, so you can play up to three. Cards you don\'t play go to your discard tonight.',
        next: 'Next',
      },
      {
        // Playtest 11: Nick remembered trashing from the tutorial, and
        // neither he nor Matt could find the button in the real game.
        kind: 'coach',
        target: '[data-tut="hall-hand"]',
        text: 'Pick up a card you don\'t want. Trashing it is under the council: it leaves your deck for good. Once a day, for a coin.',
        // The button is always on screen (disabled until a card is up), so the
        // second half waits for the card, not the button (playtest 6VNKy).
        then: { dom: '.fan__card--up', target: '[data-tut="hall-trash"]', text: 'Here it is: *Or trash it instead*. Tap it to trash, or just tap Next.' },
        next: 'Next',
      },
      {
        kind: 'coach',
        target: '[data-tut="hall-shelf"]',
        text: 'Tap *Buy a card* to see today\'s shelf. The tag under a card is its whole price: pay it once, then play it free whenever it comes round.',
        waitDom: '.shelf--open',
        skip: 'Skip',
      },
      {
        kind: 'coach',
        target: '[data-tut="hall-shelf"]',
        text: 'Tap one you can afford to buy it, or skip it today. The dark card is a burden: it pays you to take it, and then nags from your deck.',
        waitAct: 'buy',
        skip: 'Not today',
      },
      DONE_STEP,
      END('Hall', ROLE_INFO.culture.job, 'Each day: play your cards on the counselors who like them, and buy one if it helps. Keep the town happy and it keeps working.'),
    ],
  },

  trade: {
    label: 'Stewards',
    steps: [
      OPEN('Stewards'),
      FOUR('trade'),
      DAWN('trade'),
      WORKER_CARD,
      GIVE('trade'),
      START,
      {
        kind: 'card',
        title: 'What the Stewards do',
        text: 'You run the city. You put people to work in buildings, and the town lives on what they make.',
        why: 'Why it matters: food feeds the town every night, and goods and coin pay for nearly everything the other roles do.',
        next: 'Next',
      },
      {
        kind: 'card',
        title: 'Your choices',
        list: [
          ['Work a building', 'Put a shift in it. It makes what its label says.'],
          ['Build', 'A new building on an empty plot.'],
          ['Take in land', 'Six more plots for the city.'],
          ['Errands', 'Train units for the Frontier, send caravans, buy grain.'],
        ],
        next: 'Show me',
      },
      {
        kind: 'coach',
        // Playtest 11, Nick: "I can't see how many shifts I have remaining on
        // my phone." They are top left all day; point there.
        target: '[data-tut="purse-mine"]',
        text: 'Top left: your shifts left today. Each worker you are given is two shifts, half a day each. Check it as you go.',
        next: 'Next',
      },
      {
        kind: 'coach',
        target: '[data-tut="stewards-city"]',
        text: 'Tap a field on the map (it has a gold outline). A shift in a field makes food.',
        waitAct: 'work',
        skip: 'Skip',
      },
      {
        kind: 'coach',
        target: '[data-tut="stewards-city"]',
        text: 'A gold outline can still take a shift. A full building is done for today.',
        next: 'Next',
      },
      {
        kind: 'coach',
        target: '[data-tut="stewards-build"]',
        text: 'Tap *Build* to see what you can put up. It costs two shifts plus materials.',
        waitDom: '[data-tut="stewards-build-menu"]',
        skip: 'Skip',
      },
      {
        kind: 'coach',
        target: '[data-tut="stewards-build-menu"]',
        text: 'Grouped by kind. Each says its cost and what it does; greyed ones name the technology they need. A *House* lets the town grow.',
        waitAct: 'build',
        skip: 'Not now',
      },
      {
        kind: 'coach',
        target: '[data-tut="stewards-jobs"]',
        text: 'Errands happen off the map. *Train* is how the Frontier gets new units: they will ask you for them.',
        next: 'Next',
      },
      DONE_STEP,
      END('Stewards', ROLE_INFO.trade.job, 'Each day: staff the buildings, build when you can, and keep the stores up. Everyone else spends what you make.'),
    ],
  },

  science: {
    label: 'Scholars',
    steps: [
      OPEN('Scholars'),
      FOUR('science'),
      DAWN('science'),
      WORKER_CARD,
      GIVE('science'),
      START,
      // The traits come first, with an example, so "what they share" means
      // something by the time Mix is described (Matt, playtest 10).
      {
        kind: 'card',
        title: 'What the Scholars do',
        text: 'The world has eight principles. Each has three traits: *hot or cold*, *wet or dry*, *heavy or light*. Water, say, might be cold, dry and heavy.',
        why: 'Why it matters: work out each one\'s three traits and you can invent technologies, and every technology changes someone\'s board.',
        next: 'Next',
      },
      {
        kind: 'card',
        title: 'Your choices',
        list: [
          ['Mix', 'Put two principles in the flasks. The glass names the traits those two share, like "both cold".'],
          ['Notebook', 'Your deduction sheet. Cross off what each principle can\'t be.'],
          ['Research', 'Pick a technology first, then mix. The right pair invents it.'],
          ['Study, Ponder, Publish', 'Make knowledge, which pays for technologies.'],
        ],
        next: 'Show me',
      },
      {
        kind: 'card',
        title: 'The puzzle',
        text: 'One of each pair, so eight natures in all, and no two principles are alike. The names are just names: water can be dry.',
        next: 'Next',
      },
      {
        kind: 'coach',
        target: '[data-tut="sch-flasks"], .picker',
        text: 'Tap an empty flask. A box of eight principles opens under it: tap one (Fire, Water…) to pour it in. Then fill the other flask.',
        // Half done (the learner: with the box open, runs tapped the flask
        // again, which closes it).
        then: { dom: '.picker', target: '.picker', text: 'Now tap a principle in the box (Fire, Water…) to pour it into the flask.' },
        waitDom: '.swap__flag',
      },
      {
        kind: 'coach',
        target: '[data-tut="sch-mix"]',
        text: 'Tap *Mix*. It costs one worker ({workers}) and a good for the ingredients; each extra mix in a day costs one more worker.',
        waitAct: 'mix',
      },
      {
        kind: 'coach',
        target: '[data-tut="sch-glass"]',
        text: '"Both dry" means both are dry, and on the other two traits they are opposites. "Nothing in common" means opposites on all three.',
        next: 'Next',
      },
      {
        kind: 'coach',
        target: '[data-tut="sch-notebook-tab"]',
        text: 'Tap *Notebook*. That\'s your deduction sheet.',
        waitDom: '[data-tut="sch-notebook-tab"].swap__tab--on',
      },
      {
        kind: 'coach',
        target: '[data-tut="sch-sheet"]',
        text: 'A row is a principle; each column is one nature: its three traits, read down the column head. Tap a square for ✕ (can\'t be), again for ● (it is).',
        next: 'Next',
      },
      {
        kind: 'coach',
        target: '[data-tut="sch-experiment-tab"]',
        text: 'Tap *Experiment* to go back to the bench.',
        waitDom: '[data-tut="sch-experiment-tab"].swap__tab--on',
      },
      {
        kind: 'coach',
        target: '[data-tut="sch-research"]',
        text: 'To invent a technology, first tap it in this list. It says what it needs, like "both heavy". Then mix two principles that fit.',
        next: 'Next',
      },
      {
        kind: 'coach',
        target: '[data-tut="sch-more"]',
        // Say what publishing PAYS (Matt, playtest 10: "doesn't say why it matters").
        text: '*Study* or *Ponder* for knowledge. *Publish* a sure theory: if it holds, +2 knowledge and a helper for the role you name.',
        next: 'Next',
      },
      {
        kind: 'card',
        title: 'Later: the machines',
        text: 'Past tier 2, technologies are made in *machines* the Stewards build: the Kiln, then the Still, then the Crucible. Each has a hidden law to crack.',
        why: 'When the first one stands, run the machines tutorial from Help: it is short.',
        next: 'Next',
      },
      DONE_STEP,
      END('Scholars', ROLE_INFO.science.job, 'Each day: mix, mark your notebook, and when two principles fit a technology, aim at it and mix. Every invention helps someone else.'),
    ],
  },

  // THE MACHINES (the ladder, 2026-10-15; Matt: "it's going to be a little
  // bit tricky to explain, so we need to make sure we have a good
  // explanation"). Its own short practice, held as the Scholars, with a
  // Kiln already standing and a glaze already on the shelf that fits a
  // technology within reach (sandbox.js), so every step has something real.
  machines: {
    label: 'Machines',
    role: 'science',
    steps: [
      {
        kind: 'card',
        title: 'The machines',
        text: 'Another practice day, just for you: nothing here counts. Past tier 2, technologies are not mixed on the bench. They are *made* in machines the Stewards build.',
        list: [
          ['Kiln', 'Takes two of the eight principles. Makes a *glaze*. Tier 3 is made from glazes.'],
          ['Still', 'Takes two glazes. Makes an *essence*. Tier 4.'],
          ['Crucible', 'Takes two essences. Makes an *alloy*. Tier 5.'],
        ],
        next: 'Next',
      },
      {
        kind: 'card',
        title: 'Two in, one out',
        text: 'Feed a machine two things, one after the other. One new thing comes out, and you see its three traits: hot or cold, wet or dry, heavy or light.',
        why: 'A hidden *law* decides them: one rule for each trait, like "hot if either one in is hot" or "like the first one in".',
        next: 'Show me',
      },
      {
        kind: 'coach',
        target: '[data-tut="sch-machines"]',
        text: 'Tap *Machines*.',
        waitDom: '.machines',
      },
      {
        kind: 'coach',
        target: '[data-tut="mc-feed"], .machines__picker',
        text: 'Tap *first in* and pick a principle, then the same for *second in*. The Kiln takes two of the eight.',
        waitDom: '.machines__ready',
      },
      {
        kind: 'coach',
        target: '[data-tut="mc-fire"]',
        text: 'Tap *Fire the Kiln*. It costs a worker and two goods: the ingredients.',
        waitAct: 'fire',
      },
      {
        kind: 'coach',
        target: '[data-tut="mc-out"]',
        text: 'Out came a glaze, and it is on your shelf now. Compare it with what went in, one trait at a time.',
        next: 'Next',
      },
      {
        kind: 'coach',
        target: '[data-tut="mc-law"]',
        text: 'The law sheet: each trait\'s possible rules. Tap a rule to strike it if it could not have given this. Two that differ in one trait teach the most.',
        next: 'Next',
      },
      {
        kind: 'coach',
        target: '[data-tut="mc-wanted"]',
        text: 'Each technology names the glaze it needs. Your shelf holds one that fits: tap *Make*.',
        waitAct: 'make',
        skip: 'Next',
      },
      {
        kind: 'card',
        title: 'Up the ladder',
        text: 'Glazes are what the Still eats; essences are what the Crucible eats. A deep technology is a recipe: crack each law so you never waste goods on the wrong firing.',
        foot: 'Run it again any time: the menu button in the top-right corner, then Help.',
        next: 'Back to the game',
      },
    ],
  },

  military: {
    label: 'Frontier',
    steps: [
      OPEN('Frontier'),
      FOUR('military'),
      DAWN('military'),
      WORKER_CARD,
      GIVE('military'),
      START,
      {
        kind: 'card',
        title: 'What the Frontier does',
        text: 'You command the town\'s units out on the map: warbands fight, scouts see far, pioneers found villages.',
        why: 'Why it matters: new land brings food, clues and room, and you keep the raiders off everyone else.',
        next: 'Next',
      },
      {
        kind: 'card',
        title: 'Your choices',
        list: [
          ['Move', 'One action a step, two into rough ground.'],
          ['Look', '*Press and hold* any hex to see what it is.'],
          ['Camps and peoples', 'Fight them, or pay them off with coin (parley).'],
          ['Settle and work', 'A pioneer founds a village. Any unit can work the land it stands on.'],
        ],
        next: 'Show me',
      },
      {
        kind: 'coach',
        target: '[data-tut="fr-orders"]',
        text: 'Each worker you are given is two actions: a unit stepping, hunting or fighting. This is how many you have left.',
        next: 'Next',
      },
      {
        kind: 'coach',
        target: '[data-tut="fr-map"]',
        text: 'The gold ring is the unit in hand. Tap a gold hex to step there.',
        waitAct: 'move',
        skip: 'Skip',
      },
      {
        kind: 'coach',
        target: '[data-tut="fr-map"]',
        text: 'Press and hold any hex to see what it is, what it makes, and what it costs to enter.',
        next: 'Next',
      },
      {
        kind: 'coach',
        target: '[data-tut="fr-units-tab"]',
        text: 'Tap *Units* to see all your pieces.',
        waitDom: '[data-tut="fr-units-tab"].swap__tab--on',
      },
      {
        kind: 'coach',
        target: '[data-tut="fr-units"]',
        text: 'Tap one to jump to it on the map. The pioneer walks out and founds a village, which sends up what its ground makes.',
        waitDom: '[data-tut="fr-map-tab"].swap__tab--on',
        skip: 'Next',
      },
      {
        kind: 'card',
        title: 'Camps',
        text: 'Out in the fog there is a raider camp. It sends raiders until it is cleared. Walk a warband next to it and tap it: *Fight*.',
        why: 'Fight: both sides roll, each 4 or more is a hit. Parley: pay coin and it breaks up.',
        next: 'Next',
      },
      DONE_STEP,
      END('Frontier', ROLE_INFO.military.job, 'Each day: spend your actions moving units, look before you step, and deal with camps before they grow. The Stewards train new units.'),
    ],
  },
};
